import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import api from '../../../utils/api';
import buddiesService from '../../features/services/buddiesService';
import Lightbox from '../../../components/common/Lightbox';
import PhotoEngagementPanel from '../../../components/common/PhotoEngagementPanel';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { YCard } from '../../../ui/YCard';
import { YButton } from '../../../ui/YButton';
import NavigationService from '../../../services/navigationService';
import { formatLocalDate } from '../../../utils/dateUtils';
import { deriveBuddyProfileStats, deerSeenCount } from '../utils/buddyProfileStats';
import { CachedImage } from '../../../components/common/CachedImage';
import './user-profile-field.css';

function initialsFrom(name, fallback = '?') {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return fallback;
}

function periodLabel(raw) {
  if (!raw) return '';
  return String(raw).replace(/_/g, ' ');
}

function sitWhereLine(hunt) {
  const club = hunt?.club_name || '';
  const stand = hunt?.location_name || hunt?.location || '';
  if (club && stand) return `${club} · ${stand}`;
  return club || stand || '';
}

function sitDateLabel(hunt) {
  if (!hunt?.date) return '';
  return formatLocalDate(hunt.date, { month: 'short', day: 'numeric' });
}

export default function UserProfilePage() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = params.userId || location.pathname.match(/\/users\/([^/]+)/)?.[1];
  const { currentUser } = useCurrentUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [recentHunts, setRecentHunts] = useState([]);
  const [sharedClubs, setSharedClubs] = useState([]);

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await api.get(`/users/${userId}/profile/`);
      setProfile(resp.data || null);
    } catch (e) {
      setError('Failed to load profile');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadPhotos = useCallback(async () => {
    try {
      const items = await buddiesService.getUserPhotos(userId);
      setPhotos(Array.isArray(items) ? items : []);
    } catch (e) {
      setPhotos([]);
    }
  }, [userId]);

  const loadRecentHunts = useCallback((profileData) => {
    if (!profileData) return;
    const isBuddyCheck = profileData.visibility === 'buddy' || profileData.visibility === 'self';
    if (!isBuddyCheck) {
      setRecentHunts([]);
      return;
    }
    const rh = profileData.data?.recent_hunts;
    setRecentHunts(Array.isArray(rh) ? rh : []);
  }, []);

  const currentUserId = currentUser?.id;
  const myClubsKey = (currentUser?.clubs_summary || []).map((c) => String(c).toLowerCase()).join('|');

  const loadMutualConnections = useCallback((profileData) => {
    if (!profileData || currentUserId == null) return;
    const isBuddyCheck = profileData.visibility === 'buddy' || profileData.visibility === 'self';
    if (!isBuddyCheck) return;

    const profileClubs = profileData.data?.clubs_summary || [];
    const myClubs = new Set(myClubsKey ? myClubsKey.split('|') : []);
    const theirClubs = profileClubs.map((c) => String(c).toLowerCase());
    const shared = theirClubs.filter((c) => myClubs.has(c));
    setSharedClubs((prev) => {
      if (prev.length === shared.length && prev.every((v, i) => v === shared[i])) return prev;
      return shared;
    });
  }, [currentUserId, myClubsKey]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!profile) return;
    if (profile.visibility === 'buddy' || profile.visibility === 'self') {
      loadPhotos();
      loadRecentHunts(profile);
      loadMutualConnections(profile);
      return;
    }
    setPhotos([]);
    setRecentHunts([]);
    setSharedClubs([]);
  }, [profile, loadPhotos, loadRecentHunts, loadMutualConnections]);

  const currentHarvestId = photos[lightboxIndex]?.harvest_id;

  useEffect(() => {
    const targetId = location?.state?.openHarvestId;
    if (!targetId || !Array.isArray(photos) || photos.length === 0) return;
    const idx = photos.findIndex((p) => String(p.harvest_id) === String(targetId));
    if (idx >= 0) {
      setLightboxIndex(idx);
      setLightboxOpen(true);
      navigate(`/users/${userId}`, { replace: true });
    }
  }, [location?.state, photos, navigate, userId]);

  const formatRelative = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day === 1) return 'yesterday';
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/hunting-buddies');
    }
  };

  const handleMessage = () => {
    const pdata = profile?.data || profile || {};
    const name = pdata.handle || pdata.first_name || pdata.username || 'buddy';
    NavigationService.navigateToUserMessage(userId, name, navigate);
  };

  const openPhotoLightbox = (harvestId) => {
    const items = photos.map((x) => ({
      caption: x.caption || '',
      url: x.photo_url || x.photo_thumb_url,
      getUrl: async () => x.photo_url || x.photo_thumb_url,
    }));
    setLightboxItems(items);
    const idx = photos.findIndex((x) => x.harvest_id === harvestId);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-4"><Spinner /></div>
    );
  }

  if (error || !profile) {
    return (
      <div className="container-fluid p-4 pb-5">
        <YCard title="Error" headerVariant="muted">
          <div className="text-danger mb-2">{error || 'Profile not found'}</div>
          <YButton variant="secondary" size="sm" onClick={handleBack}>Go Back</YButton>
        </YCard>
      </div>
    );
  }

  const data = profile.data || profile || {};
  const isBuddy = profile.visibility === 'buddy' || profile.visibility === 'self';
  const isSelfView = currentUser && String(currentUser.id) === String(userId);
  const displayHandle = (data.handle || '').replace(/^@+/, '');
  const handleLabel = displayHandle ? `@${displayHandle}` : '@user';

  const fullName = isBuddy
    ? (`${data.first_name || ''}${data.last_name ? ` ${data.last_name}` : ''}`).trim()
    : '';
  const displayName = fullName || displayHandle || 'Hunter';
  const init = initialsFrom(fullName || displayHandle || data.username);
  const clubs = data.clubs_summary || [];
  const isCurrentlyHunting = Boolean(data.current_sign_out);
  const clubRoles = data.club_roles || [];
  const bio = data.bio || '';
  const currentSignOut = data.current_sign_out;
  const lastSignOut = data.last_sign_out;
  const method = data.preferred_hunting_method
    ? String(data.preferred_hunting_method).replace(/_/g, ' ')
    : '';
  const sharedClubSet = new Set((sharedClubs || []).map((c) => String(c).toLowerCase()));
  const statsTiles = deriveBuddyProfileStats(recentHunts, photos);

  const statusUpdates = (() => {
    if (Array.isArray(data.status_updates)) return data.status_updates;
    if (data.status_update && typeof data.status_update === 'object') return [data.status_update];
    return [];
  })();

  const latestStatusUpdate = statusUpdates
    .slice()
    .sort((a, b) => {
      const aTs = Date.parse(a?.created_at || a?.date || 0) || 0;
      const bTs = Date.parse(b?.created_at || b?.date || 0) || 0;
      return bTs - aTs;
    })[0];

  const statusUpdatedAt = isCurrentlyHunting
    ? (
      currentSignOut?.started_at
      || currentSignOut?.start_time
      || currentSignOut?.created_at
      || currentSignOut?.updated_at
    )
    : (
      latestStatusUpdate?.created_at
      || latestStatusUpdate?.date
      || latestStatusUpdate?.timestamp
      || latestStatusUpdate?.created
      || latestStatusUpdate?.updated_at
      || data.status_updated_at
      || data.status_updated
      || data.status_last_changed_at
      || data.status_last_updated
      || data.status_timestamp
      || data.status_update_timestamp
      || data.updated_at
      || profile?.updated_at
      || data.updated
      || profile?.updated
      || data.created_at
      || profile?.created_at
      || data.created
      || profile?.created
    );

  const statusTimestamp = statusUpdatedAt
    || data.updated_at
    || profile?.updated_at
    || data.updated
    || profile?.updated
    || data.created_at
    || profile?.created_at
    || data.created
    || profile?.created;

  const statusUpdatedLabel = statusTimestamp ? formatRelative(statusTimestamp) : null;

  let statusLine = '';
  if (isCurrentlyHunting && currentSignOut) {
    const where = currentSignOut.stand_name
      || currentSignOut.location_name
      || currentSignOut.location_label
      || currentSignOut.club_name
      || '';
    const period = periodLabel(currentSignOut.time_period);
    const bits = ['IN THE WOODS'];
    if (where) bits.push(String(where).toUpperCase());
    if (period) bits.push(`${period.toUpperCase()} SIT`);
    statusLine = bits.join(' · ');
  } else if (lastSignOut) {
    const when = formatRelative(lastSignOut.started_at || lastSignOut.ended_at);
    statusLine = when
      ? `BACK AT CAMP · LAST OUT ${String(when).toUpperCase()}`
      : 'BACK AT CAMP';
  } else if (data.status) {
    statusLine = data.status;
  } else {
    statusLine = 'BACK AT CAMP';
  }

  const kickerParts = [handleLabel];
  if (method) kickerParts.push(method);

  return (
    <div className="up-field" data-testid="user-profile-field">
      <header className="up-field__header" data-testid="user-profile-header">
        <div className="up-field__header-inner">
          <span className="up-field__avatar-wrap">
            {data.photo_url ? (
              <CachedImage
                src={data.photo_url}
                alt=""
                className="up-field__avatar"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <span className="up-field__avatar up-field__avatar--placeholder" aria-hidden="true">
                {init}
              </span>
            )}
            {isCurrentlyHunting ? (
              <span className="up-field__dot" title="Currently hunting" />
            ) : null}
          </span>
          <span className="up-field__identity">
            <h1 className="up-field__name">{displayName}</h1>
            <span className="up-field__kicker">{kickerParts.join(' · ')}</span>
          </span>
          <span className="up-field__header-actions">
            {isSelfView ? (
              <button
                type="button"
                className="up-field__header-btn"
                onClick={() => navigate('/profile')}
              >
                EDIT
              </button>
            ) : (
              <button
                type="button"
                className="up-field__header-btn up-field__header-btn--msg"
                aria-label="Send a message"
                onClick={handleMessage}
              >
                <span className="up-field__header-btn-label up-field__header-btn-label--full">MESSAGE</span>
                <span className="up-field__header-btn-label up-field__header-btn-label--short" aria-hidden="true">MSG</span>
              </button>
            )}
            <button
              type="button"
              className="up-field__close"
              aria-label="Close profile"
              onClick={handleBack}
            >
              ×
            </button>
          </span>
        </div>
      </header>

      <div className="up-field__inner">
        {isBuddy ? (
          <div
            className={`up-field__status${isCurrentlyHunting ? ' is-hunting' : ''}`}
            data-testid="user-profile-status"
          >
            {statusLine}
          </div>
        ) : null}

        {isBuddy && statusUpdatedLabel ? (
          <div
            className="up-field__status-updated"
            data-testid="profile-status-updated-at"
            title={statusTimestamp ? new Date(statusTimestamp).toLocaleString() : undefined}
          >
            {`Updated ${statusUpdatedLabel}`}
          </div>
        ) : null}

        {isBuddy ? (
          <div className="up-field__body">
            <div className="up-field__stats" data-testid="user-profile-stats">
              {statsTiles.map((tile) => (
                <div key={tile.label} className="up-field__stat">
                  <div className="up-field__stat-value">{tile.value}</div>
                  <div className="up-field__stat-label">{tile.label}</div>
                </div>
              ))}
            </div>

            {bio ? (
              <p className="up-field__bio" data-testid="user-profile-bio">{bio}</p>
            ) : null}

            {clubs.length > 0 ? (
              <div className="up-field__roles" data-testid="user-profile-roles">
                {clubs.map((club, idx) => {
                  const role = clubRoles.find((r) => r.club_name === club || r.club === club);
                  const isPresident = role?.role === 'president';
                  const isShared = sharedClubSet.has(String(club).toLowerCase());
                  return (
                    <span
                      key={`${club}-${idx}`}
                      className={`up-field__role${isPresident ? ' is-president' : ''}${isShared ? ' is-shared' : ''}`}
                    >
                      {isPresident ? '♛ ' : ''}
                      {club}
                    </span>
                  );
                })}
              </div>
            ) : null}

            {recentHunts.length > 0 ? (
              <section className="up-field__section" data-testid="user-profile-sits">
                <h2 className="up-field__section-title">LAST THREE SITS</h2>
                <div className="up-field__sits">
                  {recentHunts.slice(0, 3).map((hunt, idx) => {
                    const deerSeen = deerSeenCount(hunt);
                    const where = sitWhereLine(hunt);
                    const tag = hunt.harvest ? 'HARVEST' : 'SIT';
                    return (
                      <div key={hunt.id || idx} className="up-field__sit">
                        <span className="up-field__sit-date">{sitDateLabel(hunt)}</span>
                        <span className="up-field__sit-main">
                          <span className="up-field__sit-seen">
                            {deerSeen > 0 ? `${deerSeen} deer seen` : 'No deer seen'}
                            {hunt.harvest ? ' · 1 harvest' : ''}
                          </span>
                          {where ? (
                            <span className="up-field__sit-where">{where}</span>
                          ) : null}
                        </span>
                        <span className={`up-field__sit-tag${hunt.harvest ? ' is-harvest' : ''}`}>
                          {tag}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <section className="up-field__section" data-testid="user-profile-photos">
              <h2 className="up-field__section-title">HARVEST PHOTOS</h2>
              {photos.length === 0 ? (
                <div className="up-field__photos-empty">No harvest photos yet</div>
              ) : (
                <div className="up-field__photos">
                  {photos.map((p) => (
                    <button
                      key={p.harvest_id}
                      type="button"
                      className="up-field__photo"
                      onClick={() => openPhotoLightbox(p.harvest_id)}
                    >
                      <CachedImage
                        alt={p.caption || 'harvest'}
                        src={p.photo_thumb_url || p.photo_url}
                        loading="lazy"
                        decoding="async"
                      />
                      {(p.date || p.method) ? (
                        <span className="up-field__photo-label">
                          {p.date ? formatLocalDate(p.date, { month: 'short', day: 'numeric' }) : ''}
                          {p.date && p.method ? ' · ' : ''}
                          {p.method ? String(p.method).toUpperCase() : ''}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>
        ) : null}
      </div>

      {lightboxOpen ? (
        <Lightbox
          items={lightboxItems}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          rightPanel={<PhotoEngagementPanel harvestId={currentHarvestId} />}
        />
      ) : null}
    </div>
  );
}
